import { ChatMessageCitationTypename } from 'chatspot-core/__generated__/chirp/com/hubspot/copilot/rpc/v2/CopilotMessageService';
import { replaceOutsideHtmlCodeBlocks } from './markdownHtmlUtils';
const LEGACY_SOURCE_CITATION_REGEX = /\[SEARCH_?RESULT_?(\d+)\]/g;
export const SOURCE_CITATION_REGEX = /\[SEARCH_?RESULT[_:](?:[^:\]]+:)?(\d+)\]/g;
export const MEMORY_CITATION_REGEX = /\[MEMORY_?(\d+)\]/g;
export const MESSAGE_CITATION_REGEX = /\[MESSAGE_?(\d+)\]/g;
export const MEMORY_CITATION_TYPENAME = 'com.hubspot.copilot.tools.results.citations.MemoryCitation';
export const isMemoryCitation = citation => typeof (citation === null || citation === void 0 ? void 0 : citation.sourcesCount) === 'number' || (citation === null || citation === void 0 ? void 0 : citation.__typename) === MEMORY_CITATION_TYPENAME;
export const isChatMessageCitation = citation => (citation === null || citation === void 0 ? void 0 : citation.__typename) === ChatMessageCitationTypename;
export const extractCitationData = element => {
  var _element$getAttribute, _element$getAttribute2, _element$getAttribute3, _ref;
  return {
    citationArrayIndex: parseInt((_element$getAttribute = element.getAttribute('data-citation-index')) !== null && _element$getAttribute !== void 0 ? _element$getAttribute : '0', 10),
    displayNumber: (_element$getAttribute2 = element.getAttribute('data-display-number')) !== null && _element$getAttribute2 !== void 0 ? _element$getAttribute2 : '',
    index: (_element$getAttribute3 = element.getAttribute('data-index')) !== null && _element$getAttribute3 !== void 0 ? _element$getAttribute3 : '0',
    citationType: (_ref = element.getAttribute('data-citation-type')) !== null && _ref !== void 0 ? _ref : 'source'
  };
};
const COMBINED_CITATION_REGEX = /\[SEARCH_?RESULT_?\d+\]|\[MEMORY_?\d+\]|\[MESSAGE_?\d+\]/g;
export const deduplicateConsecutiveCitations = html => {
  const allCitations = [];
  replaceOutsideHtmlCodeBlocks(html, COMBINED_CITATION_REGEX, match => {
    allCitations.push(match);
    return match;
  });
  const toRemove = new Set();
  for (let i = 0; i < allCitations.length - 1; i++) {
    if (allCitations[i] === allCitations[i + 1]) {
      toRemove.add(i);
    }
  }
  if (toRemove.size === 0) return html;
  let citationCounter = 0;
  return replaceOutsideHtmlCodeBlocks(html, COMBINED_CITATION_REGEX, match => toRemove.has(citationCounter++) ? '' : match);
};
export const processSourceCitationsInHtml = (html, citations, isStreaming, isUngatedForCompoundCitationMarkers = false) => {
  const regex = isUngatedForCompoundCitationMarkers ? SOURCE_CITATION_REGEX : LEGACY_SOURCE_CITATION_REGEX;
  let citationIndex = 0;
  return replaceOutsideHtmlCodeBlocks(html, regex, (fullMatch, citationNumber) => {
    const citationArrayIndex = parseInt(citationNumber, 10) - 1;
    const citationType = 'source';
    const citation = citations === null || citations === void 0 ? void 0 : citations[citationArrayIndex];
    const displayNumber = citationNumber;
    const isCompound = isUngatedForCompoundCitationMarkers && fullMatch.includes(':');
    if (isCompound) {
      return '';
    }

    // Citation data is checked before isStreaming so that resolved citations
    // (populated via the compound registry mid-stream) render as interactive
    // inline-citations rather than always-disabled placeholders.
    if (citation) {
      return ` <inline-citation data-citation-index="${citationArrayIndex}" data-display-number="${displayNumber}" data-index="${citationIndex++}" data-citation-type="${citationType}"></inline-citation>`;
    }
    if (isStreaming) {
      return ` <citation-placeholder data-citation-index="${citationArrayIndex}" data-display-number="${displayNumber}" data-index="${citationIndex++}" data-citation-type="${citationType}"></citation-placeholder>`;
    }
    return fullMatch;
  });
};
export const processMessageCitationsInHtml = (html, messageCitations, isStreaming) => {
  let citationIndex = 0;
  return replaceOutsideHtmlCodeBlocks(html, MESSAGE_CITATION_REGEX, (fullMatch, citationNumber) => {
    const citationArrayIndex = parseInt(citationNumber, 10) - 1;
    const citationType = 'message';
    const citation = messageCitations === null || messageCitations === void 0 ? void 0 : messageCitations[citationArrayIndex];
    const displayNumber = citationNumber;
    if (citation) {
      return ` <inline-citation data-citation-index="${citationArrayIndex}" data-display-number="${displayNumber}" data-index="${citationIndex++}" data-citation-type="${citationType}"></inline-citation>`;
    }
    if (isStreaming) {
      return ` <citation-placeholder data-citation-index="${citationArrayIndex}" data-display-number="${displayNumber}" data-index="${citationIndex++}" data-citation-type="${citationType}"></citation-placeholder>`;
    }
    return fullMatch;
  });
};
export const processMemoryCitationsInHtml = (html, memoryItems = [], isStreaming) => {
  let citationIndex = 0;
  const usedFallbackIndexes = new Set();
  let fallbackIndexCursor = 0;
  const hasMemoryItem = index => memoryItems[index] !== undefined;
  const getFallbackIndex = () => {
    while (fallbackIndexCursor < memoryItems.length && (usedFallbackIndexes.has(fallbackIndexCursor) || !hasMemoryItem(fallbackIndexCursor))) {
      fallbackIndexCursor += 1;
    }
    if (fallbackIndexCursor >= memoryItems.length) {
      return null;
    }
    const fallbackIndex = fallbackIndexCursor;
    usedFallbackIndexes.add(fallbackIndex);
    fallbackIndexCursor += 1;
    return fallbackIndex;
  };
  return replaceOutsideHtmlCodeBlocks(html, MEMORY_CITATION_REGEX, (fullMatch, citationNumber) => {
    const desiredIndex = parseInt(citationNumber, 10) - 1;
    const citationArrayIndex = Number.isFinite(desiredIndex) && hasMemoryItem(desiredIndex) ? desiredIndex : getFallbackIndex();
    const citationType = 'memory';
    const displayNumber = citationNumber;
    if (citationArrayIndex === null) {
      if (!isStreaming) {
        return fullMatch;
      }
      // Citation data hasn't arrived yet mid-stream; hide the raw token
      // rather than showing it verbatim. It will be replaced once the
      // full message (with citations) is available.
      return '';
    }
    return ` <inline-citation data-citation-index="${citationArrayIndex}" data-display-number="${displayNumber}" data-index="${citationIndex++}" data-citation-type="${citationType}"></inline-citation>`;
  });
};